import $ from "jquery";
import * as _ from "underscore";
import XRegExp from "xregexp";

export interface Group {
  from: number;
  to: number;
  id: number;
  originalNodes: any; // jquery
  span: any; // jquery
}

/**
 * The number of combined text and span tags to search both before and after the translated
 * word/phrase.
 */
export const maxContextDistance = 80;

/**
 * Traverse element and extract all the span[data-p], span[data-group], and text nodes into one
 * flat array.
 */
const flatten = (element) => {
  let contentNodes = [];

  _.each(element.contents(), (node) => {
    const $node = $(node);
    if (node.nodeType === 3 || $node.is("span[data-p], span[data-group]")) {
      contentNodes.push(node);
      return;
    }
    contentNodes = contentNodes.concat(flatten($node));
  });

  return contentNodes;
};

const getPosition = (span) => {
  const position = span.attr("data-p");
  if (position) {
    return parseInt(position, 10);
  } else {
    return null;
  }
};

export const getGroup = (span, groups): Group | undefined => {
  const groupId = span.attr("data-group");

  if (groupId) {
    return groups[parseInt(groupId, 10)];
  }

  return undefined;
};

const getRange = (span, groups): { from: number; to: number } | undefined => {
  const position = getPosition(span);
  if (typeof position === "number") {
    return {
      from: position,
      to: position,
    };
  }
  const group = getGroup(span, groups);
  return group ? _.pick(group, "from", "to") : undefined;
};

export const getContextSentence = (
  span: JQuery<HTMLElement>,
  groups: Object,
  contextBeforePage: string,
  thisMaxContextDistance = maxContextDistance
): {
  nodes: {
    node: JQuery<HTMLElement>;
    text: string;
    range: { from: number; to: number };
  }[];
  text: string;
  start: number;
  end: number;
} => {
  let surroundingNodes = span.parent().contents();
  let index = -1;

  if (
    surroundingNodes.length < thisMaxContextDistance * 2 &&
    /^inline/.test(span.parent().css("display"))
  ) {
    // jump up another level and create a flattened array of surrounding
    // nodes from the ancestor's tree
    // @ts-ignore
    surroundingNodes = flatten(span.parent().parent());
  }

  $.each(surroundingNodes, (i, node) => {
    if (node === span[0]) {
      // @ts-ignore
      index = i;
    }
  });

  if (index === -1) {
    throw "error finding context";
  }

  // restrict to sentence
  let before = [];
  let i = index - 1;

  let contextStart: number;
  let contextEnd: number;

  if (span.is("[data-p]")) {
    contextStart = parseInt(span.attr("data-p"), 10);
    contextEnd = contextStart;
  } else {
    const groupId = span.attr("data-group");

    contextStart = groups[groupId].from;
    contextEnd = groups[groupId].to;
  }

  let $thisNode;
  let text;

  while (i >= 0 && index - i < thisMaxContextDistance) {
    $thisNode = $(surroundingNodes[i]);
    text = getWordText($thisNode);
    const previousNode = i > 0 ? $(surroundingNodes[i - 1]) : undefined;
    if (
      isSentenceBreak(
        `${previousNode ? getWordText(previousNode) : ""}${text}`
      ) ||
      /^(br|p)$/i.test($thisNode.prop("tagName"))
    ) {
      before.push({
        node: $thisNode,
        text,
        range: getRange($thisNode, groups),
      });
      break;
    }
    before.push({
      node: $thisNode,
      text: text,
      range: getRange($thisNode, groups),
    });
    if ($thisNode.is("[data-p]")) {
      contextStart = Math.min(
        contextStart,
        parseInt($thisNode.attr("data-p"), 10)
      );
    }
    i--;
  }

  /** First span on the page */
  const firstSpan = $("span[data-p], span[data-group]").first();

  if (
    contextBeforePage &&
    (span.is(firstSpan) || before[before.length - 1]?.node.is(firstSpan))
  ) {
    // Add the context before the page
    before.push({
      node: undefined,
      text: contextBeforePage,
    });
  }

  const after = [];

  // If the current span isn't a sentence break, add the following nodes up to and including the
  // next sentence break.
  if (!isSentenceBreak(getWordText(span))) {
    i = index + 1;
    while (i < surroundingNodes.length && i - index < thisMaxContextDistance) {
      $thisNode = $(surroundingNodes[i]);
      text = getWordText($(surroundingNodes[i]));
      if (/^(br|p)$/i.test($thisNode.prop("tagName"))) {
        break;
      }
      after.push({
        node: $thisNode,
        text: text,
        range: getRange($thisNode, groups),
      });
      const nextNode =
        i < surroundingNodes.length - 1
          ? $(surroundingNodes[i + 1])
          : undefined;
      if (isSentenceBreak(`${text}${nextNode ? getWordText(nextNode) : ""}`)) {
        break;
      }
      if ($thisNode.is("[data-p]")) {
        contextEnd = Math.max(
          contextEnd,
          parseInt($thisNode.attr("data-p"), 10)
        );
      }
      i++;
    }
  }

  before = before.reverse();

  const nodes = before
    .filter((node) => node !== undefined)
    .concat({
      node: span,
      text: getWordText(span),
      range: getRange(span, groups),
    })
    .concat(after);

  text = _.pluck(before, "text")
    .concat(getWordText(span))
    .concat(_.pluck(after, "text"))
    .join("")
    // eslint-disable-next-line no-regex-spaces
    .replace(/  /g, " ")
    .trim()
    // remove extra punctuation at end which seems to belong to the following sentence
    .replace(
      new XRegExp(
        `([${sentenceBreakCharacters.join()}])\\s+[${startOfSentencePunctuationCharacters.join()}]+$]*$`
      ),
      "$1"
    )
    // remove sentence break from the start
    .replace(new XRegExp(`^([${sentenceBreakCharacters.join()}])\\s+`), "");

  return {
    nodes,
    text,
    start: contextStart,
    end: contextEnd,
  };
};

var getWordText = (element) => {
  let text = element.text();
  let original = element.attr("data-original");

  if (original) {
    text = original;
  }

  return text;
};

// Duplicated in spannify
export const wordRegExp = () => XRegExp(`([’'\\p{L}\\p{M}]+|[0-9]+)`, "g");

export const wordRegExpWithPunctuation = () =>
  XRegExp(`([’'\\p{L}\\p{M}]+|[0-9]+|[«»"”“.,?!¡¿])`, "g");

const isSentenceBreak = (text) => {
  if (wordRegExp().test(text[0])) {
    return false;
  }

  if (sentenceBreakRegExp().test(text)) {
    return true;
  }
};

export const startOfSentencePunctuationCharacters = ["¿", "¡", "«"];

export const sentenceBreakCharacters = [".", "?", "!"];

const noSpaceSentenceBreakCharacters = ["。" /* Chinese full stop */];

export const sentenceBreakRegExp = () =>
  new RegExp(
    [
      ...sentenceBreakCharacters.map((c) => `\\${c}\\s`),
      ...noSpaceSentenceBreakCharacters.map((c) => `\\${c}`),
    ].join("|")
  );

export const lastSentenceBreakRegExp = () => {
  const allBreakCharacters = [...sentenceBreakCharacters, "n", "r"];
  return new RegExp(
    `(${[...allBreakCharacters.map((c) => `\\${c}`)].join(
      "|"
    )})[^${allBreakCharacters.map((c) => `\\${c}`).join("")}]*$`
  );
};
